import type {ComponentType, LazyExoticComponent} from 'react'
import {lazy} from 'react'

/**
 * Manages async component loading with deterministic caching.
 * Internally backed by React.lazy to preserve compiler and scheduler hints.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export class ComponentResource<T extends ComponentType<any>> {
  readonly Lazy: LazyExoticComponent<T>
  readonly #loader: () => Promise<{default: T}>
  #promise: Promise<{default: T}> | null = null
  #result: {default: T} | null = null

  constructor(loader: () => Promise<{default: T}>, resolved?: {default: T}) {
    this.#loader = loader
    if (resolved) {
      // When a synchronously-resolved module is provided (e.g., from babel plugin's
      // __sync: require('./Module')), we pre-populate both #result and #promise.
      // The loader is still stored for React.lazy fallback but won't be called
      // during normal SSR rendering since getResolved() returns immediately.
      this.#result = resolved
      this.#promise = Promise.resolve(resolved)
    }
    this.Lazy = lazy(() => this.preload())
  }

  preload(): Promise<{default: T}> {
    if (!this.#promise) {
      const loading = this.#loader()
      // Keep concurrent callers deduplicated, but allow a later preload or first render to retry
      // after a transient failure. The cached promise is cleared before the rejection reaches
      // callers, so a replacement request cannot have started yet.
      this.#promise = (async () => {
        try {
          const module = await loading
          this.#result = module
          return module
        } catch (error) {
          this.#promise = null
          throw error
        }
      })()
    }
    return this.#promise
  }

  /** Synchronous fast-path used after preload resolves. */
  getResolved(): T | null {
    return this.#result?.default ?? null
  }
}
